import { For, Match, Show, Switch, createSignal, onMount } from "solid-js";
import { Progress } from "../components/progress";
import { Link, useNavigate, useRouteData } from "@solidjs/router";
import { A } from "../components/universal/a-href";
import { ListItem, SalonSubmissionBase, Step2, UserSession } from "../types";
import { Artwork } from "../components/artwork";
import { commitChange, fetchGet } from "../library/common";
import { Button } from "solid-bootstrap";
import toast from "solid-toast";
import { loadStep2Cms } from "../app";
import { createStore } from "solid-js/store";
import { CMS } from "../components/cms/cms";
import { PageLoading } from "../components/page-loading";

type State = {
    isContentReady: boolean;
}
export function Step2Page() {
    const min = 1;
    const max = 10;
    const [session, setSession] = createStore<UserSession>({ } as UserSession);
    const [state, setState] = createStore<State>({ isContentReady: false } as State);
    const [artworks, setArtworks] = createStore<SalonSubmissionBase[]>([]);
    const [categories, setCategories] = createSignal<ListItem[]>([]);
    const count = () => artworks.length;
    const commitError = async (error: string, data?: {}) => {
        await commitChange("17th-arc-salon-application/step-2/error", {
            Error: error,
            Data: data
        });
        toast.error(error);
    }
    const onDeleteClicked = async (id: number) => {
        const params = { id };
        const response = await fetchGet("/SalonApplication/SalonSubmission/DeleteJson", params);
        const json = await response.json();
        if (!json.Success) {
            return await commitError("Unable to remove the artwork from your application at this time.", params);
        }
        const remaining = artworks.filter(x => x.id != id);
        setArtworks(remaining);
        toast.success("The artwork has been removed from your application.")
    };
    onMount(async() =>{
        const routeData = await useRouteData() as Step2;
        await loadStep2Cms();
        setSession(routeData.session);
        setArtworks(routeData.artworks);
        const _categories = routeData.resources.categories;
        setCategories(_categories);
        setState({ isContentReady: true });
    })
    return (
        <>
            <Show when={state.isContentReady} fallback={<PageLoading />}>
                <div>
                    <Progress step={2}></Progress>
                    <div class="row ">
                        <div class="col-md-8">
                            <Show when={session.isAdmin || session.canApply}>
                                <CMS id="18th-salon-application/step-2::instructions">
                                    <p style="font-size: 1.5em;">
                                        Each box below represents one entry. Click on a box to start your first submission. Once complete, you will be brought back to this page where you can submit additional entries or continue to checkout. Images should be submitted in the form of high-resolution digital images, between 1 and 15 MBs in size. Files should be in a JPG or PNG format.
                                    </p>
                                    <p>
                                        Image preparation: Multiple images of the work can be uploaded to each entry if desired, but please be sure if you are uploading images of different works of art, that you are creating a separate entry for each unique work. Only one image is required for entry for all non-sculpture works. If you are entering a sculpture, a minimum of two images are required at two different view angles. Please do not submit more then 5 images of any given work. Please submit good quality images of your work. Make sure they are not blurry. Jurors need to be able to view the details of the work. If you do not feel you have a good enough high resolution image of the work as a whole where details can be seen when magnified, please supply a detail as well as the full image. If jurors are having difficulty properly viewing the submission, additional images or details may be requested. Please crop out anything that is not part of the artwork from the primary image submitted. Additional views of the work that are submitted to show the size of the work or include an image of the artist standing with the work and/or how the work is framed are fine as supplemental images, but for the primary image, there should be no easels or frames showing. Do not make digital alterations to your photos. Basic color adjustment to try and match color and contrast to the original work is acceptable, but the image you submit needs to be as close as possible to the original work of art as viewed in person. Do not digitally remove damage that has occurred to the original work of art, do not adjust contours, erase lines or alter the composition, do not add logos or digital signatures to the images of the work. These are violations to the competition rules. In the case of sculptures, do not digitally replace the background with photos of landscapes or other settings. The photo should either have a solid black, white or gray background, a solid color cloth backdrop or be a photo of the actual installation where the work is being displayed. Images of the sculpture in the artist’s studio are also acceptable. If you have a technical problem submitting your entry, please contact our Webmaster, Iian Neill, for assistance at <A href="mailto:webmaster@artrenewal.org" class="tape">webmaster@artrenewal.org</A>. or Yvette Lytle at <A href="mailto:yvette.lytle@artrenewal.org" class="tape">yvette.lytle@artrenewal.org</A>.
                                    </p>
                                    <p>
                                        Sculptors must submit 2 images of each sculpture from different views.
                                    </p>
                                </CMS>
                            </Show>
                        </div>
                        <div class="col-md-4">
                            <div class="pull-right">
                                <div style="font-size: 1.25em;">
                                    <b style="color: goldenrod;">{count()}</b>&nbsp;
                                    <Switch>
                                        <Match when={count() == 1}>
                                            <CMS text id="label::artwork-added">artwork added</CMS>
                                        </Match>
                                        <Match when={count() != 1}>
                                            <CMS text id="label::artworks-added">artworks added</CMS>
                                        </Match>
                                    </Switch>
                                </div>
                                <div style="margin-top: 40px;">
                                    <Link class="btn btn-chunky-lg btn-chunky-dark-grey" href={"/"} style={{ "margin-right": "10px"}}>
                                        <CMS id="button::back" text>Back</CMS>
                                    </Link>
                                    <Show when={count() >= min}>
                                        <Link class="btn btn-chunky-lg btn-chunky-goldenrod" href={"/checkout"}><CMS id="button::next" text>Next</CMS></Link>
                                    </Show>
                                    <Show when={count() < min}>
                                        <Link class="btn btn-chunky-lg btn-chunky-dark-grey" href={"/checkout"}><CMS id="button::next" text>Next</CMS></Link>
                                    </Show>
                                </div>
                                <Show when={session.isAdmin || session.canApply}>
                                    <div style="margin-top: 20px;">
                                        <Button variant="primary" size="lg" href={"#/add-artwork"}><span style="margin-right: 10px;" class="fa fa-plus-circle"></span>
                                        <CMS id="button::add-artwork" text>Add Artwork</CMS></Button>
                                    </div>
                                </Show>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <For each={artworks}>{(artwork) =>
                            <Artwork
                                resources={{ categories: categories, canApply: session.canApply }}
                                session={session}
                                deleteClicked={onDeleteClicked}
                                artwork={artwork}></Artwork>
                        }</For>
                        <Show when={session.isAdmin || session.canApply}>
                            <div class="artwork-container add-artwork-container">
                                <div style="display: table; margin: 0 auto;">
                                    <div style="display: table-row;">
                                        <div style="display: table-cell; height: 300px; vertical-align: middle;">
                                            <Link class="btn btn-success" style="font-size: 2em;" href={ "/add-artwork" }>
                                                <span style="margin-right: 10px;" class="fa fa-plus-circle"></span><CMS id="button::add-artwork" text>Add Artwork</CMS>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Show>
                    </div>
                </div>
            </Show>
        </>
    )
}